import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaPlus, FaWhatsapp, FaEnvelope, FaEdit, FaPhone, FaCheckCircle, FaTimesCircle, FaLink, FaShareAlt } from 'react-icons/fa';
import { toast } from 'react-toastify';
import NewTooltip from '../../../atoms/NewTooltip';
import LabelCopy from './LabelCopy';
import { ContactDetail, getFarolColor } from './useContactPageView';
import appConfig from '../../../../config/app-config';

const COMMUNICATION_ENV = appConfig.apis.communicationApiEnv;
const BASE_URL = COMMUNICATION_ENV.address;
const API_KEY = COMMUNICATION_ENV.key;

interface ContactPointMenuHandlers {
  readonly openMenuId: string | null;
  readonly setOpenMenuId: (id: string | null) => void;
  readonly setLoadingContactPoint: (id: string) => void;
  readonly setConfirmDeleteId: (id: string | null) => void;
  readonly setConfirmDeleteLabel: (label: string) => void;
}

interface Props {
  readonly contact: ContactDetail;
  readonly contactId: string;
  readonly menuHandlers: ContactPointMenuHandlers;
  readonly onUnshareComplete?: (sharedCpId: number, action: string, restoredContactPoint?: any) => void;
}

export default function ContactAboutTab({ contact, contactId, menuHandlers, onUnshareComplete }: Props) {
  const { openMenuId, setOpenMenuId, setLoadingContactPoint, setConfirmDeleteId, setConfirmDeleteLabel } = menuHandlers;
  const navigate = useNavigate();
  const [unsharing, setUnsharing] = useState<number | null>(null);

  const handleUnshare = async (sharedCpId: number) => {
    if (unsharing) return;
    setUnsharing(sharedCpId);
    try {
      const session = await (await import('aws-amplify')).Auth.currentSession();
      const token = session.getIdToken().getJwtToken();
      const response = await fetch(`${BASE_URL}/contacts/${contactId}/shared-contact-points/${sharedCpId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', 'x-api-key': API_KEY || '', 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const result = await response.json();
        toast.success('Contato descompartilhado com sucesso!');
        onUnshareComplete?.(sharedCpId, 'unlink', result);
      }
    } catch {
      toast.error('Erro ao descompartilhar');
    } finally {
      setUnsharing(null);
    }
  };

  const formatTemplateDisplay = (cp: { templateGroups: string[]; templates: string[] }) => {
    if (cp.templateGroups?.length > 0) return cp.templateGroups.join(', ');
    if (cp.templates?.length > 0) return cp.templates.join(', ');
    return '';
  };

  return (
    <>
      <div className="grid grid-cols-2 gap-6 mb-6">
        {/* Phones */}
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium text-gray-900 dark:text-white flex items-center gap-2"><FaPhone className="text-blue-500" /> Telefones</h2>
            <NewTooltip content="Adicionar novo telefone" alignment="left">
              <button onClick={() => navigate(`/communication/contact/${contactId}/page-view/channel/phone`)} className="p-2 border border-gray-400 dark:border-gray-600 bg-gray-50 dark:bg-slate-700 text-gray-600 dark:text-gray-400 rounded-full hover:bg-gray-100 dark:hover:bg-slate-600 transition-colors"><FaPlus /></button>
            </NewTooltip>
          </div>
          <div className="space-y-4">
            {contact.phones.map((phone) => (
              <div key={phone.id} className="group border border-gray-200 dark:border-slate-700 rounded-lg p-4 relative">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      {phone.whatsapp && <FaWhatsapp className="text-green-500" size={16} />}
                      <LabelCopy text={phone.number} tooltip="Copiar telefone" className="font-medium text-gray-900 dark:text-white" />
                      {phone.primary && <span className="text-xs bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-300 px-2 py-1 rounded">Principal</span>}
                      {phone.shared && (
                        <NewTooltip content={phone.isOwner ? `Compartilhado com ${phone.sharedWith?.length || 0} empresas` : `Compartilhado por ${phone.originContact?.fullName || 'outra empresa'}`} alignment="top">
                          <span className="text-xs bg-purple-100 dark:bg-purple-900 text-purple-600 dark:text-purple-300 px-2 py-1 rounded flex items-center gap-1">
                            {phone.isOwner ? <FaShareAlt size={10} /> : <FaLink size={10} />}
                            {phone.isOwner ? 'Compartilhado' : phone.originContact?.relationshipType || 'Compartilhado'}
                          </span>
                        </NewTooltip>
                      )}
                    </div>
                    {(phone.templateGroups?.length > 0 || phone.templates?.length > 0) && <div className="text-xs text-gray-500 dark:text-gray-400 ml-6">{formatTemplateDisplay(phone)}</div>}
                    {phone.observation && <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 ml-6">{phone.observation}</p>}
                    {phone.shared && !phone.isOwner && phone.originContact && (
                      <button type="button" className="text-xs text-purple-500 dark:text-purple-400 mt-1 ml-6 cursor-pointer hover:underline bg-transparent border-none p-0" onClick={() => navigate(`/communication/contact/${phone.originContact!.contactId}/page-view`)}>
                        Compartilhado por: {phone.originContact.fullName} ({phone.originContact.relationshipType})
                      </button>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {(!phone.shared || phone.isOwner) && (
                      <div className="relative">
                        <NewTooltip content="Opções" alignment="left">
                          <button onClick={(e) => { e.stopPropagation(); setOpenMenuId(openMenuId === phone.id ? null : phone.id); }} className="opacity-0 group-hover:opacity-100 transition-opacity p-2 border border-gray-400 dark:border-gray-600 bg-gray-50 dark:bg-slate-700 text-gray-600 dark:text-gray-400 rounded-full hover:bg-gray-100 dark:hover:bg-slate-600"><FaEdit size={12} /></button>
                        </NewTooltip>
                        {openMenuId === phone.id && (
                          <div className="absolute right-0 top-10 z-50 bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-lg shadow-lg py-1 min-w-[140px]">
                            <button onClick={() => { setOpenMenuId(null); setLoadingContactPoint(phone.id); navigate(`/communication/contact/${contactId}/page-view/channel/phone/${phone.id}`, { state: { contactPoint: { id: phone.id, type: 'PHONE', value: phone.number, isActive: phone.active, isPrimary: phone.primary, isWhatsapp: phone.whatsapp, observation: phone.observation || '', templates: phone.templates || [], templateGroups: phone.templateGroups || [], shared: phone.shared, sharedContactPointId: phone.sharedContactPointId, sharedWith: phone.sharedWith || [] } } }); }} className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-600">Editar</button>
                            {!phone.shared && <button onClick={() => { setOpenMenuId(null); setConfirmDeleteId(phone.id); setConfirmDeleteLabel(`Telefone ${phone.number}`); }} className="w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-slate-600">Remover</button>}
                            {phone.shared && phone.isOwner && phone.sharedContactPointId && <button onClick={() => { setOpenMenuId(null); handleUnshare(phone.sharedContactPointId!); }} className="w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-slate-600">Descompartilhar</button>}
                          </div>
                        )}
                      </div>
                    )}
                    <NewTooltip content={phone.active ? "Ativo" : "Inativo"} alignment="top">
                      <div>{phone.active ? <FaCheckCircle className="text-green-500" size={16} /> : <FaTimesCircle className="text-gray-400" size={16} />}</div>
                    </NewTooltip>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Emails */}
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium text-gray-900 dark:text-white flex items-center gap-2"><FaEnvelope className="text-blue-500" /> Emails</h2>
            <NewTooltip content="Adicionar novo email" alignment="left">
              <button onClick={() => navigate(`/communication/contact/${contactId}/page-view/channel/email`)} className="p-2 border border-gray-400 dark:border-gray-600 bg-gray-50 dark:bg-slate-700 text-gray-600 dark:text-gray-400 rounded-full hover:bg-gray-100 dark:hover:bg-slate-600 transition-colors"><FaPlus /></button>
            </NewTooltip>
          </div>
          <div className="space-y-4">
            {contact.emails.map((email) => (
              <div key={email.id} className="group border border-gray-200 dark:border-slate-700 rounded-lg p-4 relative">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <LabelCopy text={email.address} tooltip="Copiar email" className="font-medium text-gray-900 dark:text-white" />
                      {email.primary && <span className="text-xs bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-300 px-2 py-1 rounded">Principal</span>}
                      {email.shared && (
                        <NewTooltip content={email.isOwner ? `Compartilhado com ${email.sharedWith?.length || 0} empresas` : `Compartilhado por ${email.originContact?.fullName || 'outra empresa'}`} alignment="top">
                          <span className="text-xs bg-purple-100 dark:bg-purple-900 text-purple-600 dark:text-purple-300 px-2 py-1 rounded flex items-center gap-1">
                            {email.isOwner ? <FaShareAlt size={10} /> : <FaLink size={10} />}
                            {email.isOwner ? 'Compartilhado' : email.originContact?.relationshipType || 'Compartilhado'}
                          </span>
                        </NewTooltip>
                      )}
                    </div>
                    {(email.templateGroups?.length > 0 || email.templates?.length > 0) && <div className="text-xs text-gray-500 dark:text-gray-400">{formatTemplateDisplay(email)}</div>}
                    {email.observation && <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{email.observation}</p>}
                    {email.shared && !email.isOwner && email.originContact && (
                      <button type="button" className="text-xs text-purple-500 dark:text-purple-400 mt-1 cursor-pointer hover:underline bg-transparent border-none p-0" onClick={() => navigate(`/communication/contact/${email.originContact!.contactId}/page-view`)}>
                        Compartilhado por: {email.originContact.fullName} ({email.originContact.relationshipType})
                      </button>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {(!email.shared || email.isOwner) && (
                      <div className="relative">
                        <NewTooltip content="Opções" alignment="left">
                          <button onClick={(e) => { e.stopPropagation(); setOpenMenuId(openMenuId === email.id ? null : email.id); }} className="opacity-0 group-hover:opacity-100 transition-opacity p-2 border border-gray-400 dark:border-gray-600 bg-gray-50 dark:bg-slate-700 text-gray-600 dark:text-gray-400 rounded-full hover:bg-gray-100 dark:hover:bg-slate-600"><FaEdit size={12} /></button>
                        </NewTooltip>
                        {openMenuId === email.id && (
                          <div className="absolute right-0 top-10 z-50 bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-lg shadow-lg py-1 min-w-[140px]">
                            <button onClick={() => { setOpenMenuId(null); setLoadingContactPoint(email.id); navigate(`/communication/contact/${contactId}/page-view/channel/email/${email.id}`, { state: { contactPoint: { id: email.id, type: 'EMAIL', value: email.address, isActive: email.active, isPrimary: email.primary, observation: email.observation || '', templates: email.templates || [], templateGroups: email.templateGroups || [], shared: email.shared, sharedContactPointId: email.sharedContactPointId, sharedWith: email.sharedWith || [] } } }); }} className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-600">Editar</button>
                            {!email.shared && <button onClick={() => { setOpenMenuId(null); setConfirmDeleteId(email.id); setConfirmDeleteLabel(`Email ${email.address}`); }} className="w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-slate-600">Remover</button>}
                            {email.shared && email.isOwner && email.sharedContactPointId && <button onClick={() => { setOpenMenuId(null); handleUnshare(email.sharedContactPointId!); }} className="w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-slate-600">Descompartilhar</button>}
                          </div>
                        )}
                      </div>
                    )}
                    <NewTooltip content={email.active ? "Ativo" : "Inativo"} alignment="top">
                      <div>{email.active ? <FaCheckCircle className="text-green-500" size={16} /> : <FaTimesCircle className="text-gray-400" size={16} />}</div>
                    </NewTooltip>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* About section */}
      {(() => {
        const ci = contact.clientInfo;
        const hasClientInfo = ci && Object.values(ci).some(v => v != null && v !== '');
        return (contact.observation || contact.farol || contact.nickname || contact.jobTitle || contact.birthDate || hasClientInfo) && (
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium text-gray-900 dark:text-white">Sobre este contato</h2>
            {contact.farol && <span className={`px-3 py-1 rounded-full text-sm font-medium ${getFarolColor(contact.farol)}`}>{contact.status}</span>}
          </div>
          {(contact.nickname || contact.jobTitle || contact.birthDate) && (
            <div className="grid grid-cols-3 gap-4 mb-4">
              {contact.nickname && <div><label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Apelido</label><p className="text-gray-900 dark:text-white">{contact.nickname}</p></div>}
              {contact.jobTitle && <div><label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Cargo</label><p className="text-gray-900 dark:text-white">{contact.jobTitle}</p></div>}
              {contact.birthDate && <div><label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Aniversário</label><p className="text-gray-900 dark:text-white">{contact.birthDate}</p></div>}
            </div>
          )}
          {hasClientInfo && ci && (
            <div className="grid grid-cols-2 gap-4 mb-4">
              {ci.segment && <div><label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Segmento</label><p className="text-gray-900 dark:text-white">{ci.segment}</p></div>}
              {ci.size && <div><label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Porte</label><p className="text-gray-900 dark:text-white">{ci.size}</p></div>}
              {ci.employees && <div><label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Funcionários</label><p className="text-gray-900 dark:text-white">{ci.employees}</p></div>}
              {ci.revenue && <div><label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Faturamento</label><p className="text-gray-900 dark:text-white">{ci.revenue}</p></div>}
            </div>
          )}
          {contact.observation && <div><label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Observação</label><p className="text-gray-900 dark:text-white">{contact.observation}</p></div>}
        </div>
      );
      })()}
    </>
  );
}
